import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Alert, Button, Drawer, Modal, Segmented, Select, Spin, message, Popconfirm } from 'antd';
import { HolderOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  transportApi, TransportRoute, TransportStop, StopInRoute, RouteDetail,
} from '../../api/transport';
import { L } from '../../styles/theme';

const AKSAI_CENTER: [number, number] = [51.168, 53.02];

type Direction = 'forward' | 'reverse';

interface Props {
  route: TransportRoute | null;
  allStops: TransportStop[];
  onClose: () => void;
  onSaved: () => void;
}

/* ─────────── сортируемая строка остановки ─────────── */

const SortableStopRow: React.FC<{
  stop: StopInRoute;
  index: number;
  onRemove: () => void;
}> = ({ stop, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 10px',
        fontSize: 12.5,
        color: L.text,
        borderBottom: `1px solid ${L.lineSoft}`,
        background: isDragging ? L.accentBg : L.surface,
        borderRadius: isDragging ? 6 : 0,
        zIndex: isDragging ? 10 : undefined,
        position: 'relative',
      }}
    >
      <span
        {...attributes}
        {...listeners}
        style={{ color: 'var(--l-faint)', cursor: 'grab', fontSize: 12, display: 'flex', touchAction: 'none' }}
      >
        <HolderOutlined />
      </span>
      <span className="num" style={{ fontSize: 10.5, color: L.faint, width: 18, textAlign: 'right' }}>
        {index + 1}
      </span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {stop.name_ru || stop.name_kk || `Остановка ${stop.id}`}
        {(stop.latitude == null || stop.longitude == null) && (
          <span style={{ fontSize: 10, color: L.warn, marginLeft: 6 }}>без координат</span>
        )}
      </span>
      <span
        onClick={onRemove}
        style={{ color: 'var(--l-faint)', cursor: 'pointer', fontSize: 11, display: 'flex' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = L.danger)}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--l-faint)')}
      >
        <CloseOutlined />
      </span>
    </div>
  );
};

/* ─────────── drawer ─────────── */

const RouteCompositionDrawer: React.FC<Props> = ({ route, allStops, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<RouteDetail | null>(null);
  const [direction, setDirection] = useState<Direction>('forward');
  const [lists, setLists] = useState<Record<Direction, StopInRoute[]>>({ forward: [], reverse: [] });
  const [dirty, setDirty] = useState(false);
  const [addValue, setAddValue] = useState<number | undefined>(undefined);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (!route) return;
    setLoading(true);
    setDirty(false);
    setDirection('forward');
    transportApi.getRouteDetail(route.id)
      .then(({ data }) => {
        setDetail(data);
        const fwd = data.stops
          .filter((s) => (s.direction || 'forward') === 'forward')
          .sort((a, b) => a.stop_order - b.stop_order);
        const rev = data.stops
          .filter((s) => s.direction === 'reverse')
          .sort((a, b) => a.stop_order - b.stop_order);
        setLists({ forward: fwd, reverse: rev });
      })
      .catch(() => message.error('Не удалось загрузить маршрут'))
      .finally(() => setLoading(false));
  }, [route]);

  const current = lists[direction];

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setLists((prev) => {
      const list = prev[direction];
      const from = list.findIndex((s) => s.id === active.id);
      const to = list.findIndex((s) => s.id === over.id);
      if (from < 0 || to < 0) return prev;
      return { ...prev, [direction]: arrayMove(list, from, to) };
    });
    setDirty(true);
  }, [direction]);

  const removeStop = (id: number) => {
    setLists((prev) => ({ ...prev, [direction]: prev[direction].filter((s) => s.id !== id) }));
    setDirty(true);
  };

  const addStop = (stopId: number) => {
    const stop = allStops.find((s) => s.id === stopId);
    if (!stop) return;
    if (current.some((s) => s.id === stopId)) {
      message.warning('Остановка уже есть в этом направлении');
      return;
    }
    setLists((prev) => ({
      ...prev,
      [direction]: [...prev[direction], { ...stop, stop_order: prev[direction].length + 1, direction }],
    }));
    setDirty(true);
    setAddValue(undefined);
  };

  const handleSave = async () => {
    if (!route) return;
    setSaving(true);
    try {
      const payload = (['forward', 'reverse'] as Direction[]).flatMap((dir) =>
        lists[dir].map((s, i) => ({ stop_id: s.id, stop_order: i + 1, direction: dir })),
      );
      await transportApi.replaceRouteStops(route.id, payload);
      message.success(`Состав маршрута №${route.number} сохранён — жители уже видят изменения`);
      setDirty(false);
      onSaved();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  /* полилиния активного направления из geometry_meta (track = [lon, lat]) */
  const track = useMemo<[number, number][]>(() => {
    const dirs = detail?.geometry_meta?.directions
      || (detail?.schedule as any)?.directions
      || [];
    const wanted = direction === 'forward' ? ['forward'] : ['backward', 'reverse'];
    const d = dirs.find((x: any) => wanted.includes(x.type)) || dirs[0];
    return (d?.track || []).map(([lon, lat]: [number, number]) => [lat, lon] as [number, number]);
  }, [detail, direction]);

  const stopsWithCoords = current.filter((s) => s.latitude != null && s.longitude != null);

  const options = useMemo(
    () =>
      allStops
        .filter((s) => !current.some((c) => c.id === s.id))
        .map((s) => ({
          value: s.id,
          label: `${s.name_ru || s.name_kk || 'Остановка'} · id ${s.id}`,
        })),
    [allStops, current],
  );

  return (
    <Drawer
      open={!!route}
      onClose={() => {
        if (dirty) {
          Modal.confirm({
            title: 'Закрыть без сохранения?',
            content: 'Изменения состава маршрута не сохранены и будут потеряны.',
            okText: 'Закрыть',
            okButtonProps: { danger: true },
            cancelText: 'Остаться',
            onOk: onClose,
          });
          return;
        }
        onClose();
      }}
      width={960}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>
            Маршрут №{route?.number} — состав и линия
          </span>
          {dirty && (
            <span className="pill pill-warn">
              не сохранено
            </span>
          )}
        </span>
      }
      extra={
        <Button type="primary" loading={saving} disabled={!dirty} onClick={handleSave}>
          Сохранить состав
        </Button>
      }
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, height: 'calc(100vh - 130px)' }}>
          {/* карта */}
          <div style={{ border: `1px solid ${L.line}`, borderRadius: 10, overflow: 'hidden', boxShadow: L.shadow }}>
            {route && (
              <MapContainer center={AKSAI_CENTER} zoom={13} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>, &copy; CARTO'
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
                />
                {track.length > 1 && (
                  <Polyline positions={track} pathOptions={{ color: L.accent, weight: 4, opacity: 0.85 }} />
                )}
                {stopsWithCoords.map((s, i) => (
                  <CircleMarker
                    key={s.id}
                    center={[s.latitude as number, s.longitude as number]}
                    radius={6}
                    pathOptions={{ color: L.accent, fillColor: '#fff', fillOpacity: 1, weight: 2.5 }}
                  >
                    <Tooltip direction="top" offset={[0, -6]}>
                      {i + 1}. {s.name_ru || s.name_kk || s.id}
                    </Tooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            )}
          </div>

          {/* состав */}
          <div style={{
            border: `1px solid ${L.line}`, borderRadius: 10, background: L.surface, boxShadow: L.shadow,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{ padding: '10px 12px 8px' }}>
              <Segmented
                block
                size="small"
                value={direction}
                onChange={(v) => setDirection(v as Direction)}
                options={[
                  { value: 'forward', label: `Прямое · ${lists.forward.length}` },
                  { value: 'reverse', label: `Обратное · ${lists.reverse.length}` },
                ]}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {current.length === 0 ? (
                <div style={{ padding: '24px 12px', textAlign: 'center', fontSize: 12, color: L.faint }}>
                  В этом направлении остановок нет — добавьте ниже
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={current.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    {current.map((s, i) => (
                      <SortableStopRow key={s.id} stop={s} index={i} onRemove={() => removeStop(s.id)} />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>

            <div style={{ padding: '8px 12px', borderTop: `1px solid ${L.lineSoft}` }}>
              <Select
                showSearch
                size="small"
                placeholder={<span><PlusOutlined /> Добавить остановку…</span>}
                value={addValue}
                options={options}
                onChange={(v) => addStop(v)}
                style={{ width: '100%' }}
                filterOption={(input, opt) =>
                  String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>

            {dirty && (
              <div style={{ borderTop: `1px solid ${L.lineSoft}`, padding: '9px 12px', background: 'var(--l-warn-bg)' }}>
                <div style={{ fontSize: 11, color: 'var(--l-warn)', marginBottom: 8 }}>
                  Порядок изменён. Жители увидят новый маршрут после сохранения.
                </div>
                <Popconfirm
                  title="Отменить все изменения состава?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => {
                    if (route) {
                      const fwd = (detail?.stops || [])
                        .filter((s) => (s.direction || 'forward') === 'forward')
                        .sort((a, b) => a.stop_order - b.stop_order);
                      const rev = (detail?.stops || [])
                        .filter((s) => s.direction === 'reverse')
                        .sort((a, b) => a.stop_order - b.stop_order);
                      setLists({ forward: fwd, reverse: rev });
                      setDirty(false);
                    }
                  }}
                >
                  <Button size="small" block>Отменить изменения</Button>
                </Popconfirm>
              </div>
            )}
          </div>
        </div>

        {!loading && !track.length && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 10 }}
            message="У маршрута нет линии на карте — показаны только остановки. Линия появится после импорта из 2ГИС."
          />
        )}
      </Spin>
    </Drawer>
  );
};

export default RouteCompositionDrawer;
